import { Dialog, Transition } from "@headlessui/react";
import { Fragment } from "react";
import { classNames } from "../utils/cn";
import { themeClassName } from "../utils/theme";
import { XIcon } from "lucide-react";

interface Props {
  open: boolean;
  onClose: () => void;
  children: React.ReactNode;
  size?: "sm" | "md" | "lg" | "xl" | "xxl" | "xxxl" | "xxxxl" | "full";
  position?: "top" | "center" | "bottom";
  padding?: string;
  closeOnOutsideClick?: boolean;
}

const sizesMap = {
  sm: "w-full xl:max-w-sm",
  md: "w-full xl:max-w-md",
  lg: "w-full xl:max-w-lg",
  xl: "w-full xl:max-w-2xl",
  xxl: "w-full xl:max-w-3xl",
  xxxl: "w-full xl:max-w-4xl",
  xxxxl: "w-full xl:max-w-5xl",
  full: "w-full ",
};

const positionsMap = {
  top: "items-start",
  center: "items-center",
  bottom: "items-end",
};

export function AnimatedModal(props: Props) {
  const theme = themeClassName();
  const isFullSize = props.size === "full";
  return (
    <Transition.Root show={props.open} as={Fragment}>
      <Dialog
        as="div"
        className={`relative z-[999] ${theme}`}
        onClose={(e) => {
          if (
            props.closeOnOutsideClick === undefined ||
            props.closeOnOutsideClick === true
          ) {
            props.onClose();
          }
        }}
      >
        <Transition.Child
          as={Fragment}
          enter="ease-in-out duration-50"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-100"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-600 bg-opacity-75 transition-opacity backdrop-blur-sm" />
        </Transition.Child>

        <div
          className={classNames(
            "fixed inset-0 z-20 w-screen",
            isFullSize
              ? "overflow-hidden p-8 flex items-stretch justify-center min-h-0"
              : "overflow-y-auto p-0",
          )}
        >
          <div
            className={classNames(
              props.position ? positionsMap[props.position] : "items-center",
              isFullSize
                ? "flex h-full min-h-0 max-h-full w-full justify-center text-center"
                : "flex min-h-full justify-center text-center",
              props.position && props.position === "top" ? "" : "",
            )}
          >
            <Transition.Child
              as={Fragment}
              enter="ease-in-out duration-50"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel
                className={classNames(
                  "relative transform rounded-xl bg-white text-left shadow-xl transition-all sm:w-full",
                  isFullSize
                    ? "max-h-full overflow-hidden sm:my-0 flex flex-col min-h-0"
                    : "overflow-visible sm:my-8",
                  props.size ? sizesMap[props.size] : "max-w-sm",
                  props.padding ? props.padding : "px-4 pb-4 pt-5 sm:p-6",
                )}
              >
                {props.children}
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}

AnimatedModal.Header = function AnimatedModalHeader(props: {
  title: string;
  onClose: () => void;
}) {
  return (
    <div className="rounded-t-xl bg-persian-50 px-6 py-3 flex items-center justify-between">
      <p className="text-persian-990 font-semibold text-lg">{props.title}</p>
      <XIcon
        onClick={() => {
          props.onClose();
        }}
        className="text-persian-950 w-8 h-8 hover:text-persian-800 cursor-pointer"
      />
    </div>
  );
};
